import { useEffect } from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';
import { replace } from 'lodash';
import PropTypes from 'prop-types';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FILE_SIZE, SOLR_VERSIONS } from './constants';
import { StyledButton } from './InstanceStepper.styles';
import StepTitle from './StepTitle';
import { INSTANCE_STATE } from '../../../apis/constants';
import {
  INSTANCE_CATEGORY,
  MAX_STANDARD_CIDRS_LIMIT,
} from '../../../constants';
import { isEnglish } from '../../../utils/utils';
import SelectInput from '../../elements/select-input/SelectInput';
import Text from '../../elements/text/Text';
import TextInput from '../../elements/text-input/TextInput';
import ImageUpload from '../../sections/image-upload/ImageUpload';
import CidrAddressSection from '../cidr-address/CidrAddressSection';
import AddCidrButton from '../settings/AddCidrButton';

const InstanceForm = ({ projectName }) => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    watch,
    trigger,
  } = useFormContext();

  const { name, subdomain, cidrs, plan } = watch();

  const isCommunity = plan === INSTANCE_CATEGORY.COMMUNITY;

  const formatSubdomain = (instanceName) => {
    // mataches whitespace, underscores, and hyphens. Groups them together.
    const formattedInstanceName = replace(instanceName, /[\s_-]+/g, '-');
    return `${projectName}-${formattedInstanceName}`.toLowerCase();
  };

  // Delete the last cidrs added in case plan is changed at the first step
  useEffect(() => {
    if (cidrs.length > MAX_STANDARD_CIDRS_LIMIT[plan]) {
      setValue('cidrs', cidrs.slice(0, MAX_STANDARD_CIDRS_LIMIT[plan]));
    }
  }, [plan]);

  const generateSubdomain = () => {
    const formattedSubdomain = formatSubdomain(name);
    setValue('subdomain', formattedSubdomain);
    trigger('subdomain');
  };

  const handleUpdateCidr = (cidr) => {
    setValue('cidrs', cidr);
  };

  const showGenerateSubdomain =
    name && isEnglish(projectName) && isEnglish(name) && !subdomain;

  return (
    <section>
      <StepTitle title={t('enterInstanceInfo', { ns: 'descriptions' })} />
      <Grid container alignItems="center" justifyContent="center" spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <ImageUpload name="icon" control={control} limit={FILE_SIZE.BYTES} />
        </Grid>
        <Grid
          container
          spacing={2}
          size={{
            xs: 12,
            md: 9,
          }}
        >
          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <SelectInput
              label={t('solrVersion', { ns: 'fields' })}
              controlled
              name="solrVersion"
              control={control}
              options={SOLR_VERSIONS}
              labelKey="label"
              valueKey="value"
              disabled={isCommunity}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 8,
            }}
          >
            <TextInput
              name="name"
              control={control}
              label={t('instanceName', { ns: 'fields' })}
              errors={errors}
            />
          </Grid>
          <Grid size={12}>
            <TextInput
              name="description"
              control={control}
              label={t('instanceDesc', { ns: 'fields' })}
              errors={errors}
              inputProps={{ 'aria-label': 'description', maxLength: 256 }}
            />
          </Grid>
          <Grid size={12}>
            <Controller
              name="subdomain"
              defaultValue=""
              render={({ field }) => (
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    variant="outlined"
                    size="small"
                    label={t('instanceSubdomain', { ns: 'fields' })}
                    error={!!errors?.subdomain}
                    helperText={errors?.subdomain?.message}
                    value={subdomain}
                    InputLabelProps={
                      getValues('subdomain') ? { shrink: true } : {}
                    }
                    inputProps={{ 'aria-label': 'subdomain' }}
                    fullWidth
                    {...field}
                  />

                  {showGenerateSubdomain && (
                    <StyledButton
                      variant="text"
                      onClick={generateSubdomain}
                      aria-label="generate subdomain"
                    >
                      {t('generate', { ns: 'buttons' })}
                    </StyledButton>
                  )}
                </Box>
              )}
            />
          </Grid>
        </Grid>
        <Grid
          size={12}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}
        >
          <Text bodyBold text={t('setCidr', { ns: 'instance' })} />
          <AddCidrButton
            instance={{ cidrs: cidrs }}
            maxCidrLimit={MAX_STANDARD_CIDRS_LIMIT[plan]}
            onAddCidr={handleUpdateCidr}
          />
          <CidrAddressSection
            cidrs={cidrs}
            instanceState={INSTANCE_STATE.RUNNING}
            onUpdateCidrRange={handleUpdateCidr}
            compact
            limitCidrs={MAX_STANDARD_CIDRS_LIMIT[plan]}
          />
        </Grid>
      </Grid>
    </section>
  );
};

InstanceForm.propTypes = {
  projectName: PropTypes.string.isRequired,
};

export default InstanceForm;
